import { TFunction, useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  ContentTemplate,
  CustomSelect,
  HeaderInput,
  HeaderTemplate,
  Progress,
  SectionHeader,
  TitleHeader,
  Typography,
} from 'web-library';
import { RichTextField } from 'web-library/stories/atom/RichTextField/RichTextField';

import { makeStyles } from 'tss-react/mui';
import { useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { MutationStatus } from 'react-query';
import { useHistory } from 'react-router-dom';
import { UploadedFile } from 'web-library/models/UploadedFile';
import { configurationTypeData } from '../../../modules/admin/logic/dropDownOption';
import UploadOpsAdminDocuments from './components/UploadOpsAdminDocuments';
import {
  AnnouncementConfigResponse,
  useGetAnnouncementConfig,
} from '../../../api-refactored/manage-announcements/useGetAnnouncementConfig';
import { useCreateEditAnnouncement } from './useCreateEditAnnouncement';
import RecipientValidityPeriod from './components/RecipientValidityPeriod';
import createNewAnnouncementSchema, {
  CreateNewAnnouncementData,
} from './createNewAnnouncementSchema';
import { RecipientSelectionMode } from '../../manageNotifications/SendNotification';
import { FormControlLabel } from '@mui/material';
import AnnouncementNotificationTitleTextField from 'web-library/components/AnnouncementNotificationTitleTextField';

const useStyles = makeStyles()((theme) => ({
  select: {
    marginBottom: theme.spacing(1.5),
  },
  sectionTitle: {
    marginBottom: theme.spacing(4),
  },
  radioContainer: {
    marginBottom: theme.spacing(4),
  },
  keyLabel: {
    fontSize: '1rem',
    fontWeight: 700,
    color: theme.color.text.textHeader,
  },
  upperSectionContainer: {
    marginBottom: theme.spacing(7),
  },
  announcementTextField: {
    marginBottom: theme.spacing(4),
  },
  uploadDocumentContainer: {
    marginBottom: theme.spacing(8),
  },
  dropDown: {
    marginBottom: theme.spacing(4),
  },
  lastDropDown: {
    marginBottom: theme.spacing(9),
  },
  dateContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: theme.spacing(6),
  },
  halfWidthLeftItem: {
    width: '50%',
    paddingRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      paddingRight: 0,
    },
  },
  halfWidthRightItem: {
    width: '50%',
    paddingLeft: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      paddingLeft: 0,
    },
  },
  recipientTypeContainer: {
    marginBottom: theme.spacing(8),
  },
  cancelButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(3),
    },
  },
  buttonContainer: {
    marginBottom: theme.spacing(7),
  },
  rootContainer: {
    display: 'flex',
    alignItems: 'center',
    minHeight: `${theme.spacing(6)} !important`,
    padding: '0 25px 0 16px !important',
  },
  arrowIconContainer: {
    right: '-10px !important',
  },
  chipsContainer: {
    overflowX: 'hidden',
    width: '100%',
  },
  iconButton: {
    position: 'absolute',
    right: '45px',
    top: 'calc(50% - 12px)',
    padding: 0,
  },
  chipLabel: {
    padding: 0,
  },
  errString: {
    color: theme.color.state.stateError,
    marginBottom: theme.spacing(2),
  },
}));

enum RecipientTypeMode {
  Candidate = 'Candidate',
  Coordinator = 'Coordinator',
}

const displaySectionHeaderLabel = (
  feature: string,
  isEdit: boolean,
  t: TFunction<'translation'>
) => {
  if (feature === 'ANNOUNCEMENTS') {
    return (
      <SectionHeader
        title={
          isEdit
            ? t('announcementsNotifications->>CreateNewAnnouncement.EditAnnouncement')
            : t('announcementsNotifications->>CreateNewAnnouncement.NewAnnouncement')
        }
      />
    );
  }
  if (feature === 'BROADCAST_MESSAGE') {
    return (
      <SectionHeader
        title={
          isEdit
            ? t('announcementsNotifications->>CreateNewAnnouncement.EditBroadcastMessage')
            : t('announcementsNotifications->>CreateNewAnnouncement.NewBroadcastMessage')
        }
      />
    );
  }
  if (feature === 'FAQ') {
    return (
      <SectionHeader
        title={
          isEdit
            ? t('announcementsNotifications->>CreateNewAnnouncement.EditFaq')
            : t('announcementsNotifications->>CreateNewAnnouncement.NewFaq')
        }
      />
    );
  }
  if (feature === 'ADVISORY_BANNER') {
    return (
      <SectionHeader
        title={
          isEdit
            ? t('announcementsNotifications->>CreateNewAnnouncement.EditAdvisoryBanner')
            : t('announcementsNotifications->>CreateNewAnnouncement.NewAdvisoryBanner')
        }
      />
    );
  }
  if (feature === 'PAYMENT_MESSAGE') {
    return (
      <SectionHeader
        title={
          isEdit
            ? t('announcementsNotifications->>CreateNewAnnouncement.EditPaymentMessage')
            : t('announcementsNotifications->>CreateNewAnnouncement.NewPaymentMessage')
        }
      />
    );
  }
  return null;
};

const isFieldBelowUpperSectionContainer = (announcementConfig: AnnouncementConfigResponse) =>
  announcementConfig.documentEnabled ||
  announcementConfig.recipientSelectionEnabled ||
  announcementConfig.validityPeriodEnabled;

const isFieldBelowDocumentUploadContainer = (announcementConfig: AnnouncementConfigResponse) =>
  announcementConfig.recipientSelectionEnabled || announcementConfig.validityPeriodEnabled;

interface CreateEditAnnouncementProps {
  isEdit: boolean;
}

const CreateEditAnnouncement = ({ isEdit }: CreateEditAnnouncementProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const history = useHistory<{ featureValue: string }>();

  const featureValueFromListing = history?.location?.state?.featureValue;

  const [recipientSelectionMode, setRecipientSelectionMode] = useState<RecipientSelectionMode>(
    RecipientSelectionMode.Everyone
  );

  const [recipientTypeMode, setRecipientTypeMode] = useState<RecipientTypeMode>(
    RecipientTypeMode.Candidate
  );

  const [featureValue, setFeatureValue] = useState<string>(featureValueFromListing || '');

  const [examLevelValue, setExamLevelValue] = useState<string[]>([]);

  const [isOpenExamLevelDropdown, setIsOpenExamLevelDropdown] = useState<boolean>(false);

  const [candidateTypeValue, setCandidateTypeValue] = useState<string[]>([]);

  const [isOpenCandidateTypeDropdown, setIsOpenCandidateTypeDropdown] = useState<boolean>(false);

  const [examCentreValue, setExamCentreValue] = useState<string[]>([]);

  const [isRefetchAnnouncementExamCentres, setIsRefetchAnnouncementExamCentres] = useState<boolean>(
    true
  );

  const [startDate, setStartDate] = useState<string>('');

  const [endDate, setEndDate] = useState<string>('');

  const [draftFiles, setDraftFiles] = useState<UploadedFile[]>([]);
  const [files, setFiles] = useState<UploadedFile[]>([]);

  const [apiButtonStatus, setApiButtonStatus] = useState<MutationStatus>('idle');

  const [shouldSubmit, setShouldSubmit] = useState<boolean>(false);

  const [titleText, setTitleText] = useState<string>('');

  const [descriptionText, setDescriptionText] = useState<string>('');

  const [displayDescriptionError, setDisplayDescriptionError] = useState<boolean>(false);

  const [priorityNoticeCheckbox, setPriorityNoticeCheckbox] = useState<boolean>(false);

  const [trimmedDescLength, setTrimmedDescLength] = useState<number>(0);

  const [isFetchedAnnouncementData, setIsFetchedAnnouncementData] = useState<boolean>(false);

  const [errorMessage, setErrorMessage] = useState<string>('');

  const {
    data: announcementConfig,
    isLoading: isLoadingAnnouncementConfig,
  } = useGetAnnouncementConfig({
    announcementType: featureValue,
    enabled: !!featureValue,
  });

  const formHook = useForm<CreateNewAnnouncementData>({
    mode: 'onSubmit',
    resolver: joiResolver(
      createNewAnnouncementSchema({
        announcementTitle: !announcementConfig?.titleEnabled || featureValue === 'FAQ',
        startDate: !announcementConfig?.validityPeriodEnabled,
        endDate: !announcementConfig?.validityPeriodEnabled,
      })
    ),
    defaultValues: {
      startDate,
      endDate,
    },
  });

  useEffect(() => {
    if (descriptionText) {
      setDisplayDescriptionError(false);
    }
  }, [descriptionText]);

  const {
    announcementFeature,
    announcementCandidateTypes,
    announcementExamLevels,
    announcementExamCentres,
    refetchAnnouncementExamCentres,
    isLoadingAnnouncementDetails,
    handleConfirm,
    handleBackToListing,
    AnnouncementDetails,
  } = useCreateEditAnnouncement({
    featureValue,
    files,
    candidateTypeValue,
    examLevelValue,
    examCentreValue,
    titleText,
    descriptionText,
    isEdit,
    shouldSubmit,
    priorityNoticeCheckbox,
    startDate,
    endDate,
    setRecipientSelectionMode,
    setRecipientTypeMode,
    setFeatureValue,
    setShouldSubmit,
    setFiles,
    setApiButtonStatus,
    setExamLevelValue,
    setCandidateTypeValue,
    setStartDate,
    setEndDate,
    setTitleText,
    setDraftFiles,
    setDescriptionText,
    setExamCentreValue,
    setIsRefetchAnnouncementExamCentres,
    setPriorityNoticeCheckbox,
    setIsFetchedAnnouncementData,
    setTrimmedDescLength,
    recipientSelectionMode,
    recipientTypeMode,
    formHook,
    announcementConfig,
    displayDescriptionError,
    trimmedDescLength,
    setErrorMessage,
  });

  if (
    !featureValue ||
    !candidateTypeValue ||
    isLoadingAnnouncementConfig ||
    (isLoadingAnnouncementDetails && isEdit) ||
    (AnnouncementDetails?.description && !descriptionText && !isFetchedAnnouncementData)
  ) {
    return (
      <ContentTemplate>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  const handlePriorityNoticeCheckbox = () => setPriorityNoticeCheckbox(!priorityNoticeCheckbox);

  return (
    <>
      <TitleHeader title={t('announcementsNotifications->>Listing.ManageAnnouncements')} />
      <HeaderTemplate>
        <HeaderInput
          label={t('announcementsNotifications->>Listing.Feature')}
          input={
            announcementFeature &&
            configurationTypeData(announcementFeature) && (
              <CustomSelect
                id="inputSelect-feature"
                isHeader
                menuItems={configurationTypeData(announcementFeature)}
                value={featureValue}
                disabled
                className={classes.select}
              />
            )
          }
          topBottom={false}
        />
      </HeaderTemplate>
      {announcementConfig && (
        <>
          <ContentTemplate
            isDividerBottom={isFieldBelowUpperSectionContainer(announcementConfig)}
            isContinuable={isFieldBelowUpperSectionContainer(announcementConfig)}
          >
            {displaySectionHeaderLabel(featureValue, isEdit, t)}
            <Box className={classes.upperSectionContainer}>
              {announcementConfig.titleEnabled && (
                <Box
                  className={
                    announcementConfig.descriptionEnabled ? classes.announcementTextField : ''
                  }
                >
                  <AnnouncementNotificationTitleTextField
                    formHook={formHook}
                    maxCharacterCount={announcementConfig.titleLength}
                    placeholder={t(
                      'announcementsNotifications->>CreateNewAnnouncement.TitlePlaceholder'
                    )}
                    label="Title"
                    featureValue={featureValue}
                    titleText={titleText}
                    setTitleText={setTitleText}
                  />
                </Box>
              )}
              {announcementConfig.descriptionEnabled && (
                <RichTextField
                  textValue={descriptionText}
                  setTextValue={setDescriptionText}
                  displayErrorOnEmpty={displayDescriptionError}
                  label={t('announcementsNotifications->>CreateNewAnnouncement.Description')}
                  maxCharacterCount={announcementConfig.descriptionLength}
                  placeholder={t(
                    'announcementsNotifications->>CreateNewAnnouncement.DescriptionPlaceholder'
                  )}
                  isRequired={!['BROADCAST_MESSAGE', 'ADVISORY_BANNER'].includes(featureValue)}
                  setTrimmedCharLength={setTrimmedDescLength}
                />
              )}
              {announcementConfig.priorityEnabled && (
                <FormControlLabel
                  name="priorityNoticeCheckbox"
                  control={<Checkbox checked={priorityNoticeCheckbox} />}
                  label={t(
                    'announcementsNotifications->>CreateNewAnnouncement.PriorityNoticeStatement'
                  )}
                  onChange={handlePriorityNoticeCheckbox}
                />
              )}
            </Box>
          </ContentTemplate>
          {announcementConfig.documentEnabled && (
            <ContentTemplate
              isContinuable={isFieldBelowDocumentUploadContainer(announcementConfig)}
              continuous
              isDividerBottom={isFieldBelowDocumentUploadContainer(announcementConfig)}
            >
              <Box
                className={
                  isFieldBelowDocumentUploadContainer(announcementConfig)
                    ? classes.uploadDocumentContainer
                    : ''
                }
              >
                <Typography className={classes.sectionTitle} variant="h3">
                  {t('announcementsNotifications->>CreateNewAnnouncement.UploadDocument')}
                </Typography>
                <UploadOpsAdminDocuments
                  files={files}
                  setFiles={setFiles}
                  draftFiles={draftFiles}
                  setDraftFiles={setDraftFiles}
                  isLoading={apiButtonStatus === 'loading'}
                />
              </Box>
            </ContentTemplate>
          )}
          <RecipientValidityPeriod
            classes={classes}
            featureValue={featureValue}
            announcementConfig={announcementConfig}
            isRefetchAnnouncementExamCentres={isRefetchAnnouncementExamCentres}
            endDate={endDate}
            examCentreValue={examCentreValue}
            candidateTypeValue={candidateTypeValue}
            examLevelValue={examLevelValue}
            apiButtonStatus={apiButtonStatus}
            recipientSelectionMode={recipientSelectionMode}
            setRecipientSelectionMode={setRecipientSelectionMode}
            recipientTypeMode={recipientTypeMode}
            setRecipientTypeMode={setRecipientTypeMode}
            isOpenExamLevelDropdown={isOpenExamLevelDropdown}
            isOpenCandidateTypeDropdown={isOpenCandidateTypeDropdown}
            setIsOpenExamLevelDropdown={setIsOpenExamLevelDropdown}
            startDate={startDate}
            setStartDate={setStartDate}
            setExamCentreValue={setExamCentreValue}
            setEndDate={setEndDate}
            setDisplayDescriptionError={setDisplayDescriptionError}
            setCandidateTypeValue={setCandidateTypeValue}
            setExamLevelValue={setExamLevelValue}
            setIsOpenCandidateTypeDropdown={setIsOpenCandidateTypeDropdown}
            formHook={formHook}
            refetchAnnouncementExamCentres={refetchAnnouncementExamCentres}
            announcementExamCentres={announcementExamCentres}
            handleConfirm={handleConfirm}
            handleBackToListing={handleBackToListing}
            announcementCandidateTypes={announcementCandidateTypes}
            announcementExamLevels={announcementExamLevels}
            trimmedDescLength={trimmedDescLength}
            isEdit={isEdit}
            errorMessage={errorMessage}
          />
        </>
      )}
    </>
  );
};

export default CreateEditAnnouncement;
